import type { AxiosError } from 'axios'
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { PATHS } from '@/app/router/routes'
import axios from '@/shared/api/axios'
import { FEATURES } from '@/shared/config/features'
import { API } from '@/shared/constants/api'
import { HttpStatus } from '@/shared/constants/http-status'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import useAuth from './use-auth'

/**
 * Si el error es un 401, venga del carril que venga.
 *
 * Las dos autenticaciones fallan con formas DISTINTAS y sólo se miraba una:
 *
 * - **v1 (Axios)**: el estado vive en `error.response.status`.
 * - **v2 (SDK con `throwOnError`)**: lo que se lanza es el cuerpo RFC 7807 crudo —un objeto
 *   plano sin `.response`— y el estado está en `status`, al lado de `type`/`title`/`detail`.
 *
 * Preguntando sólo por la forma de Axios, un refresh v2 vencido entraba al `catch` pero no al
 * `if`: no navegaba al login, no limpiaba la sesión y no avisaba. Devolvía `null` en silencio y
 * dejaba a la persona en una pantalla que ya no podía cargar nada.
 */
function isUnauthorized(error: unknown): boolean {
  const axiosStatus = (error as AxiosError)?.response?.status
  const problemStatus = (error as { status?: number })?.status
  return axiosStatus === HttpStatus.UNAUTHORIZED || problemStatus === HttpStatus.UNAUTHORIZED
}

const useRefreshToken = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshV2Session, setAuth } = useAuth()

  const isBoneyardBuild = typeof window !== 'undefined' && window.__BONEYARD_BUILD === true

  return useCallback(async () => {
    if (isBoneyardBuild) return null
    try {
      if (FEATURES.webAuthV2) return await refreshV2Session()

      const response = await axios.get(API.auth.refresh, {
        withCredentials: true
      })

      const dataUser = response.data

      setAuth((prev) => {
        return {
          ...prev,
          ...dataUser
        }
      })

      return dataUser.accessToken
    } catch (e) {
      console.error(e)
      if (isUnauthorized(e)) {
        navigate(PATHS.auth.login, { state: { from: location }, replace: true })
        setAuth(null)
        toast.error(getErrorMessage(e))
      }
      return null
    }
  }, [location, navigate, refreshV2Session, setAuth])
}

export default useRefreshToken
